import { auth } from 'services/firebase/client';
import { roundCurrency } from 'utils/currency';

const CLOUD_FUNCTIONS_BASE =
  'https://us-central1-clube-42-3baa5.cloudfunctions.net';

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const idToken = await auth.currentUser?.getIdToken(true);

  if (!idToken) {
    throw new Error('Token de autenticação não encontrado');
  }

  return {
    Authorization: `Bearer ${idToken}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
};

export interface PixPaymentResponse {
  id: number | string;
  status: string;
  status_detail?: string;
  payment_id?: number | string;
  point_of_interaction?: {
    transaction_data?: {
      qr_code?: string;
      qr_code_base64?: string;
      ticket_url?: string;
    };
  };
}

export interface PaymentStatusResponse {
  id: number | string;
  status: string;
  status_detail?: string;
}

export const createSubscriptionWithCard = async (param: {
  token: string;
  payer: {
    email: string;
    identification: {
      type: string;
      number: string;
    };
  };
  planId: string;
  subscriptionID?: string;
}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${CLOUD_FUNCTIONS_BASE}/createSubscriptionWithCard`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          token: param.token,
          payer: {
            email: param.payer.email,
            identification: param.payer.identification,
          },
          planId: param.planId,
          subscriptionID: param.subscriptionID || '',
        }),
      },
    );

    const result = await response.json();
    if (response.ok) {
      alert('Assinatura criada com sucesso!');
      return result;
    } else {
      alert(`Erro: ${result.message}`);
      throw new Error(result.message);
    }
  } catch (error) {
    console.error('Erro na requisição:', error);
    alert('Erro ao processar o pagamento: ' + error);
    throw error;
  }
};

export const createBeerStorePayment = async (param: {
  token: string;
  payer: {
    email: string;
    identification: {
      type: string;
      number: string;
    };
  };
  amount: number;
  cartItems: any[];
  isSubscriber?: boolean;
}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${CLOUD_FUNCTIONS_BASE}/createOneOffPayment`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          token: param.token,
          payer: {
            email: param.payer.email,
            identification: param.payer.identification,
          },
          amount: roundCurrency(param.amount),
          cartItems: param.cartItems,
          isSubscriber: param.isSubscriber || false,
        }),
      },
    );

    const result = await response.json();
    if (response.ok) {
      alert('Pagamento processado com sucesso!');
      return result;
    } else {
      alert(`Erro: ${result.message}`);
      throw new Error(result.message);
    }
  } catch (error) {
    console.error('Erro na requisição:', error);
    alert('Erro ao processar o pagamento: ' + error);
    throw error;
  }
};

export const createPixPayment = async (param: {
  amount: number;
  payer: {
    email: string;
    identification: {
      type: string;
      number: string;
    };
  };
  cartItems: unknown[];
  isSubscriber?: boolean;
  description?: string;
}): Promise<PixPaymentResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${CLOUD_FUNCTIONS_BASE}/createPixPayment`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      amount: roundCurrency(param.amount),
      payer: {
        email: param.payer.email,
        identification: param.payer.identification,
      },
      cartItems: param.cartItems,
      isSubscriber: param.isSubscriber || false,
      description: param.description || 'Clube da 42 - Loja',
    }),
  });

  const result = await response.json();

  if (response.ok) {
    return result;
  }

  throw new Error(
    result.error || result.message || 'Erro ao gerar pagamento PIX',
  );
};

export const getPaymentStatus = async (
  paymentId: string | number,
): Promise<PaymentStatusResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${CLOUD_FUNCTIONS_BASE}/getPaymentStatus?paymentId=${paymentId}`,
    {
      method: 'GET',
      headers,
    },
  );

  const result = await response.json();

  if (response.ok) {
    return result;
  }

  throw new Error(
    result.error || result.message || 'Erro ao consultar pagamento',
  );
};

export const createSubscriptionPlan = async (param: {
  reason: string;
  repetitions: number;
  transaction_amount: number;
}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${CLOUD_FUNCTIONS_BASE}/createSubscriptionPlan`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          reason: param.reason,
          repetitions: param.repetitions,
          transaction_amount: roundCurrency(param.transaction_amount),
        }),
      },
    );

    const result = await response.json();

    if (response.ok) {
      return result;
    } else {
      throw new Error(
        result.error || result.message || 'Erro ao criar plano de assinatura',
      );
    }
  } catch (error) {
    console.error('Erro na requisição:', error);
    throw error;
  }
};

export const createOrderDocument = async (payload: any) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${CLOUD_FUNCTIONS_BASE}/createOrderDocument`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      },
    );

    const result = await response.json();

    if (response.ok) {
      return result;
    } else {
      throw new Error(result.error || result.message || 'Erro ao criar pedido');
    }
  } catch (error) {
    throw error;
  }
};
